import * as React from "react";
import { type Editor, useEditorState } from "@tiptap/react";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Baseline,
  Bold,
  ChevronDown,
  Highlighter,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
  Redo2,
  RemoveFormatting,
  Strikethrough,
  Underline as UnderlineIcon,
  Unlink,
  Undo2,
} from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { RICH_TEXT_ALIGNMENTS, RICH_TEXT_HEADING_LEVELS } from "./extensions";
import {
  HIGHLIGHT_COLORS,
  TEXT_COLORS,
  normalizeHex,
  normalizeLinkHref,
  type Swatch,
} from "./palette";

const BLOCK_LABELS: Record<string, string> = {
  paragraph: "Paragraph",
  2: "Heading 1",
  3: "Heading 2",
  4: "Heading 3",
};

const DEFAULT_TEXT_COLOR = "#0f172a";

interface ToolbarButtonProps {
  label: string;
  active?: boolean;
  disabled?: boolean;
  /** Popover triggers highlight while open but are not toggles, so they skip `aria-pressed`. */
  toggle?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
}

const ToolbarButton = React.forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  ({ label, active, disabled, toggle = true, onClick, children, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={toggle ? active : undefined}
      disabled={disabled}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className={cn(
        "flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors disabled:pointer-events-none disabled:opacity-40 [&_svg]:h-4 [&_svg]:w-4",
        active
          ? "bg-[hsl(var(--color-accent))] text-[hsl(var(--color-accent-foreground))]"
          : "text-[hsl(var(--color-foreground))] hover:bg-[hsl(var(--color-accent))]",
      )}
      {...props}
    >
      {children}
    </button>
  ),
);
ToolbarButton.displayName = "ToolbarButton";

/** Groups stay intact when the toolbar wraps, so rows never break mid-cluster. */
function ToolbarGroup({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center gap-0.5">{children}</div>;
}

interface SwatchGridProps {
  swatches: Swatch[];
  active: string | null;
  onSelect: (value: string) => void;
}

function SwatchGrid({ swatches, active, onSelect }: SwatchGridProps) {
  return (
    <div className="grid grid-cols-5 gap-1.5">
      {swatches.map((swatch) => (
        <button
          key={swatch.value}
          type="button"
          title={swatch.label}
          aria-label={swatch.label}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onSelect(swatch.value)}
          className={cn(
            "h-7 w-full rounded-md border transition-shadow",
            active === swatch.value
              ? "border-[hsl(var(--color-foreground))] ring-1 ring-[hsl(var(--color-foreground))]"
              : "border-[hsl(var(--color-border))] hover:ring-1 hover:ring-[hsl(var(--color-ring))]",
          )}
          style={{ backgroundColor: swatch.value }}
        />
      ))}
    </div>
  );
}

interface ColorPopoverProps {
  label: string;
  resetLabel: string;
  swatches: Swatch[];
  active: string | null;
  indicatorColor: string;
  icon: React.ReactNode;
  onSelect: (value: string) => void;
  onReset: () => void;
}

function ColorPopover({
  label,
  resetLabel,
  swatches,
  active,
  indicatorColor,
  icon,
  onSelect,
  onReset,
}: ColorPopoverProps) {
  const [open, setOpen] = React.useState(false);
  const [custom, setCustom] = React.useState("");
  const customHex = normalizeHex(custom);

  const choose = (value: string) => {
    onSelect(value);
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setCustom("");
      }}
    >
      <PopoverTrigger asChild>
        <ToolbarButton label={label} active={open} toggle={false}>
          <span className="flex flex-col items-center gap-0.5">
            {icon}
            <span className="h-1 w-4 rounded-full" style={{ backgroundColor: indicatorColor }} />
          </span>
        </ToolbarButton>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-3">
        <SwatchGrid swatches={swatches} active={active} onSelect={choose} />
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            onReset();
            setOpen(false);
          }}
          className="mt-2 w-full rounded-md border border-[hsl(var(--color-border))] py-1.5 text-xs font-medium transition-colors hover:bg-[hsl(var(--color-accent))]"
        >
          {resetLabel}
        </button>
        <div className="mt-3 flex items-center gap-2">
          <Input
            value={custom}
            onChange={(event) => setCustom(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== "Enter" || !customHex) return;
              event.preventDefault();
              choose(customHex);
            }}
            placeholder="#3366ff"
            className="h-8 flex-1 text-xs"
            spellCheck={false}
          />
          <Button
            type="button"
            size="sm"
            disabled={!customHex}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => customHex && choose(customHex)}
          >
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface LinkPopoverProps {
  editor: Editor;
  active: boolean;
  href: string;
}

function LinkPopover({ editor, active, href }: LinkPopoverProps) {
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState("");
  const inputRef = React.useRef<HTMLInputElement>(null);
  const normalized = normalizeLinkHref(value);

  const apply = () => {
    if (!normalized) return;
    const { empty } = editor.state.selection;

    if (empty && !active) {
      editor
        .chain()
        .focus()
        .insertContent({
          type: "text",
          text: normalized,
          marks: [{ type: "link", attrs: { href: normalized } }],
        })
        .run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href: normalized }).run();
    }

    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setValue(href);
      }}
    >
      <PopoverTrigger asChild>
        <ToolbarButton label={active ? "Edit link" : "Add link"} active={active || open} toggle={false}>
          <LinkIcon />
        </ToolbarButton>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 p-3"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          inputRef.current?.focus();
          inputRef.current?.select();
        }}
      >
        <div className="flex items-center gap-2">
          <Input
            ref={inputRef}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== "Enter") return;
              event.preventDefault();
              apply();
            }}
            placeholder="https://example.com"
            className="h-8 flex-1 text-xs"
            spellCheck={false}
          />
          <Button
            type="button"
            size="sm"
            disabled={!normalized}
            onMouseDown={(event) => event.preventDefault()}
            onClick={apply}
          >
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export interface RichTextToolbarProps {
  editor: Editor;
}

export function RichTextToolbar({ editor }: RichTextToolbarProps) {
  const state = useEditorState({
    editor,
    selector: ({ editor: current }) => {
      const alignment = RICH_TEXT_ALIGNMENTS.find((value) =>
        current.isActive({ textAlign: value }),
      );

      return {
        bold: current.isActive("bold"),
        italic: current.isActive("italic"),
        underline: current.isActive("underline"),
        strike: current.isActive("strike"),
        bulletList: current.isActive("bulletList"),
        orderedList: current.isActive("orderedList"),
        blockquote: current.isActive("blockquote"),
        link: current.isActive("link"),
        href: (current.getAttributes("link").href as string | undefined) ?? "",
        color: (current.getAttributes("textStyle").color as string | undefined) ?? null,
        highlight: (current.getAttributes("highlight").color as string | undefined) ?? null,
        heading:
          RICH_TEXT_HEADING_LEVELS.find((level) => current.isActive("heading", { level })) ?? null,
        alignment: alignment ?? "left",
        canUndo: current.can().undo(),
        canRedo: current.can().redo(),
      };
    },
  });

  const setBlock = (level: number | null) => {
    const chain = editor.chain().focus();
    if (level === null) {
      chain.setParagraph().run();
      return;
    }
    chain.setHeading({ level: level as 2 | 3 | 4 }).run();
  };

  const blockLabel = BLOCK_LABELS[state.heading ?? "paragraph"];

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-[hsl(var(--color-border))] px-2 py-1.5">
      <ToolbarGroup>
        <ToolbarButton label="Undo" disabled={!state.canUndo} onClick={() => editor.chain().focus().undo().run()}>
          <Undo2 />
        </ToolbarButton>
        <ToolbarButton label="Redo" disabled={!state.canRedo} onClick={() => editor.chain().focus().redo().run()}>
          <Redo2 />
        </ToolbarButton>
      </ToolbarGroup>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            className="flex h-8 w-32 shrink-0 items-center justify-between rounded-md px-2 text-sm transition-colors hover:bg-[hsl(var(--color-accent))]"
          >
            {blockLabel}
            <ChevronDown className="h-3.5 w-3.5 opacity-60" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-32">
          <DropdownMenuItem onSelect={() => setBlock(null)}>Paragraph</DropdownMenuItem>
          {RICH_TEXT_HEADING_LEVELS.map((level) => (
            <DropdownMenuItem key={level} onSelect={() => setBlock(level)}>
              {BLOCK_LABELS[level]}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <ToolbarGroup>
        <ToolbarButton label="Bold" active={state.bold} onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold />
        </ToolbarButton>
        <ToolbarButton label="Italic" active={state.italic} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Italic />
        </ToolbarButton>
        <ToolbarButton
          label="Underline"
          active={state.underline}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon />
        </ToolbarButton>
        <ToolbarButton
          label="Strikethrough"
          active={state.strike}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <Strikethrough />
        </ToolbarButton>
      </ToolbarGroup>

      <ToolbarGroup>
        <ColorPopover
          label="Text colour"
          resetLabel="Default colour"
          swatches={TEXT_COLORS}
          active={state.color}
          indicatorColor={state.color ?? DEFAULT_TEXT_COLOR}
          icon={<Baseline />}
          onSelect={(value) => editor.chain().focus().setColor(value).run()}
          onReset={() => editor.chain().focus().unsetColor().run()}
        />
        <ColorPopover
          label="Highlight"
          resetLabel="No highlight"
          swatches={HIGHLIGHT_COLORS}
          active={state.highlight}
          indicatorColor={state.highlight ?? "transparent"}
          icon={<Highlighter />}
          onSelect={(value) => editor.chain().focus().setHighlight({ color: value }).run()}
          onReset={() => editor.chain().focus().unsetHighlight().run()}
        />
      </ToolbarGroup>

      <ToolbarGroup>
        <ToolbarButton
          label="Bullet list"
          active={state.bulletList}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List />
        </ToolbarButton>
        <ToolbarButton
          label="Numbered list"
          active={state.orderedList}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered />
        </ToolbarButton>
        <ToolbarButton
          label="Quote"
          active={state.blockquote}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          <Quote />
        </ToolbarButton>
      </ToolbarGroup>

      <ToolbarGroup>
        <ToolbarButton
          label="Align left"
          active={state.alignment === "left"}
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
        >
          <AlignLeft />
        </ToolbarButton>
        <ToolbarButton
          label="Align centre"
          active={state.alignment === "center"}
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
        >
          <AlignCenter />
        </ToolbarButton>
        <ToolbarButton
          label="Align right"
          active={state.alignment === "right"}
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
        >
          <AlignRight />
        </ToolbarButton>
      </ToolbarGroup>

      <ToolbarGroup>
        <LinkPopover editor={editor} active={state.link} href={state.href} />
        <ToolbarButton
          label="Remove link"
          disabled={!state.link}
          onClick={() => editor.chain().focus().extendMarkRange("link").unsetLink().run()}
        >
          <Unlink />
        </ToolbarButton>
        <ToolbarButton
          label="Clear formatting"
          onClick={() => editor.chain().focus().unsetAllMarks().unsetTextAlign().clearNodes().run()}
        >
          <RemoveFormatting />
        </ToolbarButton>
      </ToolbarGroup>
    </div>
  );
}
